'use memo';

import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { HStack, VStack } from '@/components/Layout';
import { AddStepperNavigation, AddStepperStepTitleCard } from '@/components/Stepper';
import DockAddStepperAction from '@/components/Stepper/DockAddStepperAction';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { formatDateToLocalISOString } from '@/utils/dateFormat';
import { IdentityStep } from './-components/IdentityStep';
import { ItemDefinitionStep } from './-components/ItemDefinitionStep';
import { StatusStep } from './-components/StatusStep';
import { DEFAULT_PAYROLL_ITEM_ADD_STEP, PAYROLL_ITEM_ADD_LINK, PAYROLL_ITEM_ADD_STEPS, type PayrollItemAddStep } from './-constants';
import { payrollItemAddKeyPrefix } from './-lib/keyPrefix';
import { useCreatePayrollItem, usePayrollItemCodes } from './-query';
import type { PayrollItemAddDraft } from './-types';
import { emptyPayrollItemAddDraft, findAllIssues, findStepIssues, isPayrollItemAddStep, nextStep, previousStep, stepNumber } from './-utils/payrollItemAddDraft';
export const Route = createFileRoute('/_staff/payroll-engine-mock/_layout/items/add/')({
  component: PayrollItemAddPage
});

/** 有効開始日の初期値（yyyy-MM-dd）。UTC 変換で前日にならないようローカル日付で作る。 */
function today(): string {
  return formatDateToLocalISOString(new Date());
}
function PayrollItemAddPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: payrollItemAddKeyPrefix
  });
  useDocumentTitle(t($ => $.title));
  const location = useLocation();
  const navigate = useNavigate();
  const rawHash = location.hash.replace('#', '');
  // ハッシュ無し・未知のハッシュは最初のステップとして扱う。
  const currentStep: PayrollItemAddStep = isPayrollItemAddStep(rawHash) ? rawHash : DEFAULT_PAYROLL_ITEM_ADD_STEP;
  const [draft, setDraft] = useState<PayrollItemAddDraft>(() => emptyPayrollItemAddDraft(today()));
  const updateDraft = (patch: Partial<PayrollItemAddDraft>) => {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  };
  const createMutation = useCreatePayrollItem();
  const {
    data: existingCodes
  } = usePayrollItemCodes();
  /**
   * 送信の再入ガード。`createMutation.isPending` はレンダー結果なので、
   * 同一 tick の 2 回目のクリックでは まだ false のままボタンが押せてしまう
   * （ダブルクリックで同じ項目が 2 件作られる）。ハンドラ側で ref を見て弾く。
   */
  const isSubmittingRef = useRef(false);
  const stepLabels = useMemo((): Record<PayrollItemAddStep, string> => ({
    identity: t($ => $.steps.identity),
    'item-definition': t($ => $.steps.itemDefinition),
    status: t($ => $.steps.status)
  }), [t]);
  const steps = useMemo(() => PAYROLL_ITEM_ADD_STEPS.map(step => ({
    label: stepLabels[step],
    hash: step
  })), [stepLabels]);
  const previous = previousStep(currentStep);
  const next = nextStep(currentStep);
  // 最終ステップの「完了」は全ステップの入力が揃ってから押せるようにする。
  const blockingIssues = next === null ? findAllIssues(draft, existingCodes) : findStepIssues(currentStep, draft, existingCodes);
  const firstIssue = blockingIssues[0];
  const handleNextClick = () => {
    if (next !== null) {
      void navigate({
        to: PAYROLL_ITEM_ADD_LINK,
        hash: next
      });
    }
  };
  const handleCompleteClick = () => {
    // ボタンの disabled はレンダー後に効くため、同一 tick の連打はここで弾く。
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    createMutation.mutate(draft, {
      // 失敗時は再試行できるよう必ず解除する（トーストを出して同じ画面に留まる）。
      onSettled: () => {
        isSubmittingRef.current = false;
      }
    });
  };
  return <HStack className="w-full gap-6 p-6">
      {/*
       * 左ナビカードは Figma どおりフォーム下端まで伸ばす。
       * `AddStepperNavigation` の Card は `h-full`（= height:100%）を持つため、
       * 高さ auto の行では stretch されず内容なりの高さになる。
       * stretch された wrapper（高さが確定する）を 1 枚挟んで 100% を解決させる。
       * `items-start` / `sticky`（`self-start`）はどちらも stretch を外すので使わない。
       */}
      <div className="flex self-stretch">
        <AddStepperNavigation title={t($ => $.title)} baseLink={PAYROLL_ITEM_ADD_LINK} currentHash={currentStep} steps={steps} />
      </div>

      <VStack className="w-full max-w-[680px] gap-6">
        <AddStepperStepTitleCard stepName={`${stepNumber(currentStep)}. ${stepLabels[currentStep]}`} />

        {match(currentStep).with('identity', () => <IdentityStep draft={draft} onChange={updateDraft} />).with('item-definition', () => <ItemDefinitionStep draft={draft} onChange={updateDraft} />).with('status', () => <StatusStep draft={draft} onChange={updateDraft} />).exhaustive()}

        <DockAddStepperAction baseLink={PAYROLL_ITEM_ADD_LINK} previousPage={previous} nextPage={next} nextComplete={next === null} disabled={firstIssue !== undefined || createMutation.isPending} isLoading={createMutation.isPending} onNextClick={handleNextClick} onCompleteClick={handleCompleteClick} />
      </VStack>
    </HStack>;
}